import {
  DatabaseReference,
  get,
  off,
  onValue,
  ref,
  remove,
  set,
  update,
} from "firebase/database";
import store from "store";
import {
  UserForceMoveData,
  setUserForceMove,
  resetUserForceMove,
  setUserForceMoveData,
  setShowUserForceMovePopup,
} from "modules/userForceMove/redux/slice";
import {
  BUFFER_COUNT_DOWN,
  EventUserNotificationType,
} from "modules/userForceMove/constants";
import {
  getCurrentEventId,
  getCurrentUserId,
  getEventManagerIds,
} from "helpers/reduxHelper";
import { database } from "../firebaseConfig";
import { EVENT_NOTIFICATION_PATH } from "./constants";

export const createEventUserNotification = (
  payload: Omit<UserForceMoveData, "createdAt" | "managerId">,
) => {
  const eventId = getCurrentEventId();
  const managerId = getCurrentUserId();

  if (!eventId || !managerId) {
    return;
  }

  const createdAt = Date.now();

  const dbRef = ref(database, `${EVENT_NOTIFICATION_PATH}/${eventId}`);
  const data = {
    ...payload,
    createdAt,
    managerId,
  };

  set(dbRef, data);
  store.dispatch(setUserForceMoveData(data));
};

export const updateEventUserNotification = async ({
  type,
}: Pick<UserForceMoveData, "type">) => {
  const eventId = getCurrentEventId();

  if (!eventId) {
    return;
  }

  const dbRef = ref(database, `${EVENT_NOTIFICATION_PATH}/${eventId}`);
  const dbData = await get(dbRef);

  if (!dbData.exists()) {
    return;
  }

  await update(dbRef, {
    type,
  });

  const updatedData = { ...dbData.val(), type };

  store.dispatch(setUserForceMoveData(updatedData));
};

export const deleteEventUserNotification = () => {
  const eventId = getCurrentEventId();

  const dbRef = ref(database, `${EVENT_NOTIFICATION_PATH}/${eventId}`);
  remove(dbRef);
};

export const getUserForceMoveCountDown = (createdAt: number): number => {
  const currentTime = Date.now();
  const bufferDiff = (currentTime - createdAt) / 1000;

  return BUFFER_COUNT_DOWN - Math.ceil(bufferDiff);
};

export const checkExistingRecord = async (
  ref: DatabaseReference,
  userId: string,
) => {
  const data = await get(ref);
  const managerIds = getEventManagerIds();

  const notification: UserForceMoveData = data.val();

  if (
    !notification ||
    (notification && ![notification.userId, ...managerIds].includes(userId))
  ) {
    return;
  }

  const countDown = getUserForceMoveCountDown(notification.createdAt);

  if (countDown < 0) {
    remove(ref);
  }
};

export const subscribeEventUserNotification = async (eventId: string) => {
  const userId = getCurrentUserId();

  if (!userId) {
    return;
  }

  const dbRef = ref(database, `${EVENT_NOTIFICATION_PATH}/${eventId}`);

  // check for old records, if there is any we delete it.
  await checkExistingRecord(dbRef, userId);

  off(dbRef);

  onValue(dbRef, async (snapshot) => {
    const notification: UserForceMoveData = snapshot.val();

    // reset data if notification is cancelled and deleted or yet to be created
    if (!notification) {
      store.dispatch(resetUserForceMove());
      return;
    }

    const managerIds = getEventManagerIds();

    // store data only for managers and moved user
    if (![notification.userId, ...managerIds].includes(userId)) {
      return;
    }

    const countDown = getUserForceMoveCountDown(notification.createdAt);

    store.dispatch(setUserForceMoveData(notification));

    // if confirm open pop up for both manager and user
    if (
      notification.type === EventUserNotificationType.CONFIRM_MOVE &&
      countDown > 0
    ) {
      store.dispatch(setShowUserForceMovePopup(true));

      return;
    }

    // trigger api call right away
    if (
      notification.type === EventUserNotificationType.IMMEDIATE_MOVE ||
      notification.type === EventUserNotificationType.EXECUTE_DELAY_MOVE ||
      countDown < 0
    ) {
      store.dispatch(setUserForceMove(true));
    }
  });
};

export const unsubscribeEventUserNotification = (eventId: string) => {
  const dbRef = ref(database, `${EVENT_NOTIFICATION_PATH}/${eventId}`);
  off(dbRef);
};
